import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import api from "../lib/api";
import { ART_MAP } from "../components/TileArts";
import useJsonLd from "../lib/useJsonLd";

const SITE_URL = "https://luckynightteer.com";

// Nine tiles matching the reference grid — order and labels are exact
// Each tile may be internal (to) or external (href → opens in new tab)
const TILES = [
  { key: "common-number", label: "Common Number", to: "/common-number" },
  { key: "blog", label: "Blog", to: "/blog" },
  { key: "dream-number", label: "Dream Number", to: "/dream-number" },
  {
    key: "predict-number",
    label: "Predict Number",
    href: "https://whatsapp.com/channel/0029Vb8MzS9F1YlWpvY4M81x",
  },
  {
    key: "predict-target",
    label: "Predict Target",
    href: "https://youtube.com/@morningteerliveresult?si=EVVZwViXcUBP7RGt",
  },
  { key: "previous-result", label: "Previous Result", to: "/previous-result" },
  { key: "teer-calendar", label: "Teer Calendar", to: "/teer-calendar" },
  {
    key: "reputed-counter",
    label: "Reputed Counter",
    href: "https://www.facebook.com/share/18nZVtunj6/",
  },
  { key: "night-teer-app", label: "Night Teer App", to: "/night-teer-app" },
];

function Tile({ tile }) {
  const Art = ART_MAP[tile.key];
  const common = {
    "data-testid": `tile-${tile.key}`,
    className: "nt-tile block aspect-square relative overflow-hidden bg-white",
    "aria-label": tile.label,
  };
  const inner = (
    <>
      <Art />
      <span className="nt-tile-caption" data-testid={`tile-label-${tile.key}`}>
        {tile.label}
      </span>
    </>
  );
  if (tile.href) {
    return (
      <a
        href={tile.href}
        target="_blank"
        rel="noreferrer noopener"
        {...common}
      >
        {inner}
      </a>
    );
  }
  return (
    <Link to={tile.to} {...common}>
      {inner}
    </Link>
  );
}

export default function Home() {
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/results/latest");
      setLatest(data && data.id ? data : null);
    } catch {
      setLatest(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Rich JSON-LD for the daily result — updates when latest changes
  useJsonLd(
    "home-result",
    latest && latest.id
      ? {
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          "headline": `Night Teer Result ${latest.date} — F/R ${latest.fr || "--"}, S/R ${latest.sr || "--"}`,
          "description": `Official Lucky Night Teer Result for ${latest.date}. First Round (${latest.fr_time || "10:00 PM"}) = ${latest.fr || "--"}, Second Round (${latest.sr_time || "11:00 PM"}) = ${latest.sr || "--"}.`,
          "datePublished":
            latest.created_at || new Date().toISOString(),
          "dateModified":
            latest.created_at || new Date().toISOString(),
          "author": { "@type": "Organization", "name": "Lucky Night Teer" },
          "publisher": {
            "@type": "Organization",
            "name": "Lucky Night Teer",
            "logo": { "@type": "ImageObject", "url": `${SITE_URL}/logo.svg` },
          },
          "mainEntityOfPage": { "@type": "WebPage", "@id": `${SITE_URL}/` },
          "image": `${SITE_URL}/logo.svg`,
        }
      : null
  );

  // ItemList for the 9 tiles (helps sitelinks)
  useJsonLd("home-itemlist", {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": TILES.map((t, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": t.label,
      "url": t.href || `${SITE_URL}${t.to}`,
    })),
  });

  return (
    <PublicLayout title="Lucky Night Teer">
      <div className="nt-container mt-4">
        {/* Date centered */}
        <div
          className="text-center font-display text-xl sm:text-2xl mb-3"
          data-testid="live-date"
        >
          {latest?.date || "—"}
        </div>

        {/* Simple result table matching reference */}
        <table
          className="nt-table mx-auto max-w-md"
          data-testid="live-result-table"
        >
          <thead>
            <tr>
              <th
                colSpan={2}
                className="!bg-slate-100 !text-slate-900 text-lg py-2"
              >
                {latest?.city || "Lucky Teer"}
              </th>
            </tr>
            <tr>
              <th>F/R ({latest?.fr_time || "10:00 PM"})</th>
              <th>S/R ({latest?.sr_time || "11:00 PM"})</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-3">
                {loading ? (
                  <div className="h-6 mx-8 rounded bg-slate-100 animate-pulse" />
                ) : (
                  <span className="nt-num" data-testid="live-fr">
                    {latest?.fr || "xx"}
                  </span>
                )}
              </td>
              <td className="py-3">
                {loading ? (
                  <div className="h-6 mx-8 rounded bg-slate-100 animate-pulse" />
                ) : (
                  <span className="nt-num" data-testid="live-sr">
                    {latest?.sr || "xx"}
                  </span>
                )}
              </td>
            </tr>
          </tbody>
        </table>

        {latest?.text ? (
          <p
            className="text-center text-sm text-slate-800 font-semibold mt-2"
            data-testid="live-text"
          >
            {latest.text}
          </p>
        ) : null}

        {/* Grid of Tiles */}
        <section className="mt-8" data-testid="home-tiles">
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            {TILES.map((t) => (
              <Tile key={t.key} tile={t} />
            ))}
          </div>
        </section>
      </div>
    </PublicLayout>
  );
}
